// src/pages/hr/recruitment/CandidateProfile.jsx
import React, { useState } from "react";
import { updateCandidateStage, createOffer } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";

export default function CandidateProfile({ candidate, onClose, onRefresh }) {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");

  // Offer form
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerData, setOfferData] = useState({
    salary: "",
    joiningDate: "",
    reportingManager: "",
    benefits: "Health Insurance, Paid Leaves, Performance Bonus",
    notes: "",
  });

  if (!candidate) return null;

  async function handleStageChange(newStage) {
    await updateCandidateStage(candidate.id, newStage, userProfile?.displayName || "HR Admin", candidate.jobId);
    if (onRefresh) onRefresh();
  }

  async function handleGenerateOffer(e) {
    e.preventDefault();
    await createOffer({
      candidateId: candidate.id,
      candidateName: candidate.name,
      candidateEmail: candidate.email,
      jobId: candidate.jobId || "",
      jobTitle: candidate.appliedPosition || candidate.role || "",
      ...offerData,
      createdByName: userProfile?.displayName || "HR",
    });
    await updateCandidateStage(candidate.id, "Offer", userProfile?.displayName || "HR", candidate.jobId);
    setShowOfferModal(false);
    alert("Offer generated successfully!");
    if (onRefresh) onRefresh();
  }

  return (
    <div className="modal-overlay">
      <style>{`
        .prof-modal { background: var(--bg-card, #fff); border-radius: 14px; width: 100%; max-width: 900px; max-height: 92vh; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 10px 40px rgba(0,0,0,0.25); }
        .prof-header { background: linear-gradient(135deg, #1b4b31, #2d7a50); color: #fff; padding: 1.25rem 1.5rem; display: flex; justify-content: space-between; align-items: flex-start; }
        .prof-avatar { width: 56px; height: 56px; border-radius: 50%; background: #fff; color: #1b4b31; font-weight: 800; font-size: 1.3rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .prof-nav { display: flex; gap: 0.5rem; background: var(--bg-secondary, #f9fafb); padding: 0.5rem 1.5rem; border-bottom: 1px solid var(--border, #e5e7eb); overflow-x: auto; }
        .prof-tab { padding: 0.5rem 1rem; border: none; background: none; font-size: 0.83rem; font-weight: 600; cursor: pointer; color: var(--text-muted); border-bottom: 3px solid transparent; white-space: nowrap; }
        .prof-tab.active { color: #1b4b31; border-bottom-color: #1b4b31; }
        .prof-body { padding: 1.5rem; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 1rem; }
        .prof-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        @media(max-width: 650px) { .prof-grid-2 { grid-template-columns: 1fr; } }
        .prof-item { display: flex; justify-content: space-between; padding: 0.45rem 0; border-bottom: 1px solid var(--border, #f3f4f6); font-size: 0.82rem; }
        .prof-lbl { color: var(--text-muted); }
        .prof-val { font-weight: 600; color: var(--text-primary, #111); text-align: right; }
        .timeline-list { display: flex; flex-direction: column; gap: 0.75rem; border-left: 2px solid #1b4b31; padding-left: 1rem; margin-left: 0.5rem; }
        .timeline-item { position: relative; }
        .timeline-item::before { content: ""; position: absolute; left: -1.35rem; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: #1b4b31; }
      `}</style>

      <div className="prof-modal">
        {/* Header */}
        <div className="prof-header">
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <div className="prof-avatar">
              {candidate.name?.slice(0, 2)?.toUpperCase() || "C"}
            </div>
            <div>
              <div style={{ fontSize: "1.2rem", fontWeight: 700 }}>{candidate.name}</div>
              <div style={{ fontSize: "0.8rem", opacity: 0.85 }}>
                {candidate.appliedPosition || candidate.role || "Applicant"} · {candidate.email || "No email"}
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: 6, alignItems: "center" }}>
                <span style={{ background: "rgba(255,255,255,0.2)", padding: "0.15rem 0.6rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700 }}>
                  Stage: {candidate.stage || "Applied"}
                </span>
                {candidate.aiScore && (
                  <span style={{ background: "#10b981", color: "#fff", padding: "0.15rem 0.6rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700 }}>
                    AI Match: {candidate.aiScore}%
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="btn-secondary" style={{ padding: "0.3rem 0.6rem", color: "#fff", background: "rgba(255,255,255,0.2)", border: "none" }} onClick={onClose}>
            ✕ Close
          </button>
        </div>

        {/* Sub-nav */}
        <div className="prof-nav">
          {[
            { id: "overview", label: "👤 Overview" },
            { id: "resume", label: "📄 Resume & Skills" },
            { id: "timeline", label: "⏱️ Timeline" },
            { id: "offer", label: "💼 Offer & Hiring" },
          ].map((t) => (
            <button key={t.id} className={`prof-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="prof-body">
          {activeTab === "overview" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h4 style={{ margin: 0, fontWeight: 700 }}>Candidate Profile Details</h4>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <select
                    className="ats-select"
                    style={{ fontSize: "0.78rem" }}
                    value={candidate.stage}
                    onChange={(e) => handleStageChange(e.target.value)}
                  >
                    {[
                      "Applied", "Screening", "Assessment", "Interview",
                      "Manager Review", "HR Review", "Offer", "Background Verification", "Hired", "Rejected", "Hold"
                    ].map((s) => (
                      <option key={s} value={s}>Move to: {s}</option>
                    ))}
                  </select>
                  <button className="btn-primary" style={{ padding: "0.3rem 0.75rem", fontSize: "0.78rem" }} onClick={() => setShowOfferModal(true)}>
                    📝 Generate Offer
                  </button>
                </div>
              </div>

              <div className="prof-grid-2">
                <div>
                  <div className="prof-item"><span className="prof-lbl">Phone</span><span className="prof-val">{candidate.phone || "—"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Current Company</span><span className="prof-val">{candidate.currentCompany || "—"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Experience</span><span className="prof-val">{candidate.experience || "—"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Notice Period</span><span className="prof-val">{candidate.noticePeriod || "—"}</span></div>
                </div>
                <div>
                  <div className="prof-item"><span className="prof-lbl">Expected Salary</span><span className="prof-val">{candidate.expectedSalary || "—"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Current Salary</span><span className="prof-val">{candidate.currentSalary || "—"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Source</span><span className="prof-val">{candidate.source || "Direct Portal"}</span></div>
                  <div className="prof-item"><span className="prof-lbl">Recruiter</span><span className="prof-val">{candidate.recruiter || "HR Team"}</span></div>
                </div>
              </div>

              {candidate.notes && (
                <div style={{ marginTop: "1rem", background: "var(--bg-secondary)", padding: "0.85rem", borderRadius: 8, fontSize: "0.82rem" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Recruiter Notes:</div>
                  {candidate.notes}
                </div>
              )}
            </div>
          )}

          {activeTab === "resume" && (
            <div>
              <h4 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Skills & Experience Profile</h4>
              <div style={{ marginBottom: "1rem" }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-muted)", marginBottom: 6 }}>Key Skills:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                  {(candidate.skills || ["React", "JavaScript", "HTML/CSS"]).map((sk) => (
                    <span key={sk} style={{ background: "#d1fae5", color: "#065f46", padding: "0.2rem 0.6rem", borderRadius: 99, fontSize: "0.75rem", fontWeight: 600 }}>
                      {sk}
                    </span>
                  ))}
                </div>
              </div>
              {candidate.resumeUrl ? (
                <a href={candidate.resumeUrl} target="_blank" rel="noreferrer" className="btn-primary" style={{ display: "inline-block" }}>
                  📄 View Uploaded Resume PDF
                </a>
              ) : (
                <div style={{ background: "var(--bg-secondary)", padding: "1.5rem", borderRadius: 8, textStyle: "italic", fontSize: "0.82rem", color: "var(--text-muted)" }}>
                  No external resume PDF uploaded. Candidate profile skills generated automatically.
                </div>
              )}
            </div>
          )}

          {activeTab === "timeline" && (
            <div>
              <h4 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Hiring Stage Movement Timeline</h4>
              <div className="timeline-list">
                {(candidate.stageHistory || [{ stage: "Applied", movedAt: new Date().toISOString(), movedBy: "System" }]).map((h, idx) => (
                  <div key={idx} className="timeline-item">
                    <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>Moved to {h.stage}</div>
                    <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>
                      by {h.movedBy} on {new Date(h.movedAt).toLocaleString("en-IN")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "offer" && (
            <div>
              <h4 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Offer & Onboarding Sign-off</h4>
              <p style={{ fontSize: "0.83rem", color: "var(--text-muted)" }}>
                When candidate accepts offer and moves to <strong>Hired</strong> stage, an official Employee Account is automatically initialized in the database.
              </p>
              <button className="btn-primary" onClick={() => setShowOfferModal(true)}>
                + Create / View Offer Letter
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Offer Modal */}
      {showOfferModal && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div className="modal-body" style={{ maxWidth: 500 }}>
            <h3 style={{ margin: "0 0 1rem", fontWeight: 700 }}>Generate Offer Letter</h3>
            <form onSubmit={handleGenerateOffer} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Offered CTC / Annual Salary (INR) *</label>
                <input
                  type="number"
                  className="ats-input"
                  style={{ width: "100%" }}
                  required
                  placeholder="e.g. 1200000"
                  value={offerData.salary}
                  onChange={(e) => setOfferData({ ...offerData, salary: e.target.value })}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Expected Joining Date *</label>
                <input
                  type="date"
                  className="ats-input"
                  style={{ width: "100%" }}
                  required
                  value={offerData.joiningDate}
                  onChange={(e) => setOfferData({ ...offerData, joiningDate: e.target.value })}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Reporting Manager</label>
                <input
                  type="text"
                  className="ats-input"
                  style={{ width: "100%" }}
                  placeholder="e.g. Team Lead Name"
                  value={offerData.reportingManager}
                  onChange={(e) => setOfferData({ ...offerData, reportingManager: e.target.value })}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowOfferModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Generate & Send Offer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
